<template>
    <div class="refund-financial-block">
        <div class="block-header">
            <h3 class="block-title">Refund Options</h3>
            <p class="block-description">
                Choose how you'd like to proceed with your refund request.
            </p>
        </div>

        <div class="refund-options">
            <div class="refund-option-card" @click="selectPauseRenewal">
                <div class="card-header">
                    <svg class="w-6 h-6 mr-3 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                    </svg>
                    <h4 class="card-title">Pause Auto-Renewal</h4>
                </div>
                <div class="card-content">
                    <p class="card-description">Keep your current license but stop future automatic renewals.</p>
                    <p class="card-note">
                        You'll keep access to services until your current billing period ends.
                    </p>
                </div>
            </div>
            
            <div class="refund-option-card" @click="selectFullRefund">
                <div class="card-header">
                    <svg class="w-6 h-6 mr-3 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd" />
                    </svg>
                    <h4 class="card-title">Full Refund</h4>
                </div>
                <div class="card-content">
                    <p class="card-description">Request a full refund of your license payment.</p>
                    <p class="card-note">
                        Your license will be cancelled immediately and you'll receive a refund within 5-10 business days.
                    </p>
                </div>
            </div>
        </div>

        <div class="action-buttons">
            <button 
                class="btn btn-outline"
                @click="$emit('goToPage', 2)"
            >
                Back
            </button>
            <button 
                class="btn btn-outline"
                @click="$emit('close')"
            >
                Cancel
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';

const emit = defineEmits<{
    close: [];
    goToPage: [page: number];
    tookOffer: [];
    cancelAutoRenew: [];
}>();

const selectedOption = ref('');

const selectFullRefund = () => {
    selectedOption.value = 'full_refund';
    emit('goToPage', 4);
};

const selectPauseRenewal = () => {
    selectedOption.value = 'pause_renewal';
    emit('cancelAutoRenew');
};


</script>

<style lang="scss" scoped>
.refund-financial-block {
    @apply p-6;
}

.block-header {
    @apply mb-6 text-center;
    
    .block-title {
        @apply text-2xl font-semibold text-gray-900 mb-2;
    }
    
    .block-description {
        @apply text-gray-600 text-base;
    }
}

.refund-options {
    @apply mb-6 space-y-4;
}

.refund-option-card {
    @apply bg-white rounded-lg border border-gray-200 p-6 transition-all duration-200 cursor-pointer;
    
    &:hover {
        @apply shadow-md border-gray-300 bg-gray-50;
    }
    
    &:active {
        @apply transform scale-95;
    }
    
    .card-header {
        @apply flex items-center mb-4;
        
        .card-title {
            @apply text-lg font-semibold text-gray-900;
        }
    }
    
    .card-content {
        .card-description {
            @apply text-gray-900 mb-2;
        }
        
        .card-note {
            @apply text-sm text-gray-600;
        }
    }
}

.action-buttons {
    @apply flex justify-center gap-3;
    
    .btn {
        @apply px-6 py-2 rounded-lg font-medium transition-all duration-200;
        
        &.btn-outline {
            @apply border border-gray-300 text-gray-700 bg-white;
            
            &:hover {
                @apply border-gray-400 bg-gray-50;
            }
        }
        
        &.btn-primary {
            @apply bg-blue-500 text-white border border-blue-500;
            
            &:hover {
                @apply bg-blue-600 border-blue-600;
            }
        }
        
        &.btn-warning {
            @apply bg-orange-500 text-white border border-orange-500;
            
            &:hover {
                @apply bg-orange-500 border-orange-500;
            }
        }
        
        &.btn-success {
            @apply bg-green-500 text-white border border-green-500;
            
            &:hover {
                @apply bg-green-600 border-green-600;
            }
        }
    }
}
</style> 